/**
 * The YouTube channel bound to a Show.
 *
 * The binding lives on the Show rather than on the user because a Show *is*
 * the programme: "one channel per Show" is what `docs/phases/phase-06.md`
 * asks for, and it is what makes a Live able to find its RTMP target without
 * anybody choosing a channel at broadcast time.
 *
 * Nothing in this file is a secret. The refresh and access tokens never leave
 * the backend - see `packages/backend/src/services/youtube.ts`.
 */

/** The public face of a connected channel, as YouTube reports it. */
export interface YouTubeChannel {
  /** The `UC...` channel id. */
  id: string;
  title: string;
  /** The `@handle`, when the channel has one. */
  customUrl: string | null;
  thumbnailUrl: string | null;
}

/** An established binding between a Show and a channel. */
export interface YouTubeConnection {
  showId: string;
  channel: YouTubeChannel;
  /** The OAuth scopes Google actually granted, which may be fewer than asked. */
  scopes: string[];
  /** ISO 8601 timestamp. */
  connectedAt: string;
  /** ISO 8601 timestamp. */
  updatedAt: string;
}

/**
 * What the Show settings screen needs to draw the YouTube card.
 *
 * `configured` is about the *deployment*, not the Show: false means this
 * server has no Google OAuth client, so there is nothing to connect to and the
 * UI should say so instead of offering a button that cannot work.
 */
export interface YouTubeConnectionStatus {
  configured: boolean;
  connection: YouTubeConnection | null;
}

/** Where to send the browser to begin the consent flow. */
export interface YouTubeAuthUrl {
  authUrl: string;
}

/**
 * Scopes requested at consent time.
 *
 * Asked for in full on the first bind even though this phase only reads the
 * channel: adding a scope later forces every connected Show back through the
 * consent screen, and a re-consent in the middle of Phase 6 would be a
 * self-inflicted regression.
 *
 * - `youtube` manages broadcasts and streams (6.2).
 * - `youtube.force-ssl` reads and writes live chat (6.3).
 */
export const YOUTUBE_SCOPES = [
  'https://www.googleapis.com/auth/youtube',
  'https://www.googleapis.com/auth/youtube.force-ssl',
] as const;

/** Outcome of the OAuth round trip, handed back to the app as a query param. */
export const YOUTUBE_CALLBACK_PARAM = 'youtube';
export const YOUTUBE_CALLBACK_RESULTS = ['connected', 'denied', 'failed'] as const;
export type YouTubeCallbackResult = (typeof YOUTUBE_CALLBACK_RESULTS)[number];
